import React, { useState, useEffect } from 'react';
import { abdmApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Fingerprint, Buildings, CheckCircle, XCircle, ShieldCheck,
  ToggleRight, ToggleLeft, Warning,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ABDMAdminPage = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await abdmApi.getHospitalsStatus();
      setHospitals(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggle = async (tenantId, module, currentValue) => {
    try {
      await abdmApi.toggleHospital(tenantId, { module, enabled: !currentValue });
      toast.success(`${module.toUpperCase()} ${!currentValue ? 'enabled' : 'disabled'}`);
      loadData();
    } catch (e) { toast.error('Failed to toggle'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" /></div>;

  const abdmEnabled = hospitals.filter(h => h.abdm_enabled).length;
  const nhcxEnabled = hospitals.filter(h => h.nhcx_enabled).length;
  const abdmConfigured = hospitals.filter(h => h.abdm_configured).length;

  return (
    <div className="space-y-5" data-testid="abdm-admin-page">
      <Toaster position="top-right" richColors />

      <div className="bg-gradient-to-r from-teal-900 via-slate-900 to-blue-900 rounded-2xl p-6 text-white" data-testid="abdm-admin-header">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-teal-500/20 flex items-center justify-center border border-teal-500/30">
            <Fingerprint className="h-6 w-6 text-teal-400" weight="duotone" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit']">ABDM / NHCX Management</h1>
            <p className="text-sm text-slate-300 mt-0.5">Enable and monitor ABDM integration across hospitals</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="bg-slate-50 border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total Hospitals</p>
          <p className="text-2xl font-bold font-['Outfit'] text-slate-700 mt-1">{hospitals.length}</p>
        </CardContent></Card>
        <Card className="bg-teal-50 border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">ABDM Enabled</p>
          <p className="text-2xl font-bold font-['Outfit'] text-teal-600 mt-1">{abdmEnabled}</p>
        </CardContent></Card>
        <Card className="bg-blue-50 border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">NHCX Enabled</p>
          <p className="text-2xl font-bold font-['Outfit'] text-blue-600 mt-1">{nhcxEnabled}</p>
        </CardContent></Card>
        <Card className="bg-amber-50 border-0 shadow-sm"><CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Setup Pending</p>
          <p className="text-2xl font-bold font-['Outfit'] text-amber-600 mt-1">{abdmEnabled - abdmConfigured}</p>
        </CardContent></Card>
      </div>

      {/* Hospitals Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="hospitals-abdm-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hospital</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">ABDM</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Configured</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Mode</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">NHCX</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">ABHA Mandatory</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {hospitals.map(h => (
                <TableRow key={h.hospital_id} className="hover:bg-teal-50/30" data-testid={`hospital-${h.hospital_id}`}>
                  <TableCell>
                    <p className="text-sm font-medium text-slate-800">{h.hospital_name}</p>
                    <p className="text-[10px] text-slate-400">{h.city}</p>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${h.abdm_enabled ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'} border-0 text-[10px]`}>
                      {h.abdm_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {h.abdm_configured ? <CheckCircle className="h-4 w-4 text-emerald-500 mx-auto" weight="fill" /> : <XCircle className="h-4 w-4 text-slate-300 mx-auto" />}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${h.abdm_sandbox ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'} border-0 text-[9px]`}>
                      {h.abdm_sandbox ? 'Sandbox' : 'Production'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${h.nhcx_enabled ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'} border-0 text-[10px]`}>
                      {h.nhcx_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {h.abha_mandatory ? <Badge className="bg-violet-100 text-violet-700 border-0 text-[9px]">Mandatory</Badge> : <span className="text-xs text-slate-400">Optional</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="outline" size="sm" className={`h-7 text-[10px] gap-1 ${h.abdm_enabled ? 'border-red-200 text-red-500' : 'border-teal-200 text-teal-600'}`}
                        onClick={() => toggle(h.hospital_id, 'abdm', h.abdm_enabled)} data-testid={`toggle-abdm-${h.hospital_id}`}>
                        {h.abdm_enabled ? <ToggleLeft className="h-3 w-3" /> : <ToggleRight className="h-3 w-3" />}
                        ABDM
                      </Button>
                      <Button variant="outline" size="sm" className={`h-7 text-[10px] gap-1 ${h.nhcx_enabled ? 'border-red-200 text-red-500' : 'border-blue-200 text-blue-600'}`}
                        onClick={() => toggle(h.hospital_id, 'nhcx', h.nhcx_enabled)} data-testid={`toggle-nhcx-${h.hospital_id}`}>
                        {h.nhcx_enabled ? <ToggleLeft className="h-3 w-3" /> : <ToggleRight className="h-3 w-3" />}
                        NHCX
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* No patient data notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
        <Warning className="h-4 w-4 text-amber-600 shrink-0" />
        <p className="text-xs text-amber-700">Super Admin can only manage integration status. No patient, claim, or ABHA data is accessible from this view (HIPAA compliant).</p>
      </div>
    </div>
  );
};

export default ABDMAdminPage;
